// Persian presentation helpers for the admin console (design admin-taskcoolV1 is Persian-only, RTL).
// Instants arrive as ISO-8601 UTC; dates are shown in the Persian (Jalali) calendar like the reference strings
// ("فروردین ۱۴۰۲", "۱۴ مهر ۱۴۰۵", "۱۲ دقیقه پیش").

/** Reference `fa()`: Persian digits with Persian grouping. */
export const fa = (n: number | string): string => Number(n).toLocaleString('fa-IR');

const P = 'fa-IR-u-ca-persian';
const parse = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : null;
};

/** Relative time like the reference sample: همین حالا / ۱۲ دقیقه پیش / ۳ ساعت پیش / دیروز / ۴ روز پیش / ۲ هفته پیش / ۱ ماه پیش. */
export function ago(iso: string | null | undefined, now = Date.now()): string {
  const t = parse(iso);
  if (t === null) return '—';
  const m = Math.floor((now - t) / 60000);
  if (m < 1) return 'همین حالا';
  if (m < 60) return fa(m) + ' دقیقه پیش';
  const h = Math.floor(m / 60);
  if (h < 24) return fa(h) + ' ساعت پیش';
  const d = Math.floor(h / 24);
  if (d === 1) return 'دیروز';
  if (d < 7) return fa(d) + ' روز پیش';
  if (d < 30) return fa(Math.floor(d / 7)) + ' هفته پیش';
  if (d < 365) return fa(Math.floor(d / 30)) + ' ماه پیش';
  return fa(Math.floor(d / 365)) + ' سال پیش';
}

/** Whole days elapsed since an instant (trash countdown). */
export const daysSince = (iso: string, now = Date.now()): number => {
  const t = parse(iso);
  return t === null ? 0 : Math.max(0, Math.floor((now - t) / 86400000));
};

/** "فروردین ۱۴۰۲" (membership month). */
export const monthYear = (iso: string | null | undefined): string => {
  const t = parse(iso);
  return t === null ? '—' : new Date(t).toLocaleDateString(P, { month: 'long', year: 'numeric', timeZone: 'Asia/Tehran' });
};

/** "۱۴ مهر ۱۴۰۵" (release dates; date-only values are read as calendar dates). */
export const fullDate = (isoOrDate: string | null | undefined): string => {
  if (!isoOrDate) return '—';
  const t = parse(/^\d{4}-\d{2}-\d{2}$/.test(isoOrDate) ? isoOrDate + 'T12:00:00Z' : isoOrDate);
  return t === null ? '—' : new Date(t).toLocaleDateString(P, { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Asia/Tehran' });
};

/** Chart labels: weekday ("شنبه"), day + month ("۱۸ شهریور"), month ("مهر"). */
export const weekday = (iso: string): string => new Date(Date.parse(iso)).toLocaleDateString(P, { weekday: 'long', timeZone: 'Asia/Tehran' });
export const dayMonth = (iso: string): string => new Date(Date.parse(iso)).toLocaleDateString(P, { day: 'numeric', month: 'long', timeZone: 'Asia/Tehran' });
export const monthName = (iso: string): string => new Date(Date.parse(iso)).toLocaleDateString(P, { month: 'long', timeZone: 'Asia/Tehran' });

/** Storage like the reference sample ("۱٫۲ گ", "۸۴۰ م", "۰"); null = not measured. */
export function size(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '—';
  if (bytes <= 0) return fa(0);
  const mb = bytes / 1048576;
  if (mb < 1) return fa(Math.max(1, Math.round(bytes / 1024))) + ' ک';
  if (mb < 1024) return fa(Math.round(mb)) + ' م';
  return fa(Math.round(mb / 102.4) / 10) + ' گ';
}

/** Gigabytes with one decimal for the storage card ("۳۲۰ از ۵۰۰ گیگابایت"). */
export const gb = (bytes: number): string => {
  const g = bytes / 1073741824;
  return fa(g >= 100 ? Math.round(g) : Math.round(g * 10) / 10);
};

/** Signed percentage change ("+۸٪", "−۲٪"); null when there is nothing to compare with. */
export function pctChange(cur: number, prev: number): { text: string; up: boolean } | null {
  if (!prev) return null;
  const p = Math.round(((cur - prev) / prev) * 100);
  return { text: (p >= 0 ? '+' : '−') + fa(Math.abs(p)) + '٪', up: p >= 0 };
}

/** Date and time in Tehran ("۱۶ مهر ۱۴۰۵، ۱۰:۳۰"; English: "16 Oct 2026, 10:30 Tehran"). */
export function dateTime(iso: string | null | undefined, lang: 'fa' | 'en' = 'fa'): string {
  const t = parse(iso);
  if (t === null) return '—';
  const o: Intl.DateTimeFormatOptions = { day: 'numeric', month: lang === 'fa' ? 'long' : 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: 'Asia/Tehran' };
  return lang === 'fa' ? new Date(t).toLocaleString(P, o) : new Date(t).toLocaleString('en-GB', o) + ' Tehran';
}
